import { HorizontalScrollable } from '@dsx-react/scrollable';
import { useControllableState, classNames } from '@dsx-react/util';
import { createContext, useMemo, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import { Button } from '@dsx-react/button';
import { MenuItem } from '@dsx-react/menu';

const TabContext = /*#__PURE__*/createContext({});

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Tabs-module_tab-list__4Ii1C {\n  gap: var(--size-100);\n  padding: var(--size-200) var(--size-100);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_variant-default__eu0d9 {\n  --border-color: var(--color-neutral-200);\n  --selection-indicator-color: var(--color-primary-500);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_variant-inverse__uvF9Q {\n  --border-color: rgba(var(--color-neutral-0-rgb), 0.2);\n  --selection-indicator-color: var(--color-neutral-0);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-default__v82Wi {\n  --selection-indicator-height: 3px;\n  --inset-selection-indicator-bottom: 0.3125rem;\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-compact__aYhv8,\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-small__30BGQ,\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-small-compact__V52Bx {\n  --selection-indicator-height: 2px;\n  --inset-selection-indicator-bottom: 0.125rem;\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_border__NqdiS::before {\n  content: \"\";\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  border-bottom: 1px solid var(--border-color);\n}\n\n.Tabs-module_tab__ugp4j {\n  position: relative;\n  white-space: nowrap;\n}\n\n.Tabs-module_tab__ugp4j::after {\n  content: \"\";\n  position: absolute;\n  bottom: calc(-1 * var(--size-200));\n  height: var(--selection-indicator-height);\n  border-radius: 1px 1px 0 0;\n  transition: var(--UNSAFE-button-transition-default) ease border;\n}\n\n.Tabs-module_tab__ugp4j.Tabs-module_selected__IxA81::after {\n  background-color: var(--selection-indicator-color);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_inset-selection-indicator__rAAj8 .Tabs-module_tab__ugp4j::after {\n  border-radius: 9999px;\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-default__v82Wi .Tabs-module_tab__ugp4j::after {\n  left: var(--UNSAFE-button-default-padding);\n  right: var(--UNSAFE-button-default-padding);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-compact__aYhv8 .Tabs-module_tab__ugp4j::after {\n  left: var(--UNSAFE-button-compact-padding);\n  right: var(--UNSAFE-button-compact-padding);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-small__30BGQ .Tabs-module_tab__ugp4j::after {\n  left: var(--UNSAFE-button-small-padding);\n  right: var(--UNSAFE-button-small-padding);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_size-small-compact__V52Bx .Tabs-module_tab__ugp4j::after {\n  left: var(--UNSAFE-button-small-compact-padding);\n  right: var(--UNSAFE-button-small-compact-padding);\n}\n\n.Tabs-module_tab-list__4Ii1C.Tabs-module_inset-selection-indicator__rAAj8 .Tabs-module_tab__ugp4j::after {\n  bottom: var(--inset-selection-indicator-bottom);\n}\n\n.Tabs-module_arrow-left__BqBnw,\n.Tabs-module_arrow-right__cYsZO {\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  transition: opacity 300ms ease;\n  pointer-events: none;\n}\n.Tabs-module_arrow-left__BqBnw:not(.Tabs-module_hidden__ZLDLC) button,\n.Tabs-module_arrow-right__cYsZO:not(.Tabs-module_hidden__ZLDLC) button {\n  pointer-events: auto;\n}\n.Tabs-module_arrow-left__BqBnw.Tabs-module_hidden__ZLDLC,\n.Tabs-module_arrow-right__cYsZO.Tabs-module_hidden__ZLDLC {\n  opacity: 0;\n}\n\n.Tabs-module_arrow-left__BqBnw {\n  left: 0;\n  padding-right: 1rem;\n  background: linear-gradient(to left, transparent, var(--color-gray-0) 1rem);\n}\n\n.Tabs-module_arrow-right__cYsZO {\n  right: 0;\n  padding-left: 1rem;\n  background: linear-gradient(to right, transparent, var(--color-gray-0) 1rem);\n}";
var styles = {"tab-list":"Tabs-module_tab-list__4Ii1C","variant-default":"Tabs-module_variant-default__eu0d9","variant-inverse":"Tabs-module_variant-inverse__uvF9Q","size-default":"Tabs-module_size-default__v82Wi","size-compact":"Tabs-module_size-compact__aYhv8","size-small":"Tabs-module_size-small__30BGQ","size-small-compact":"Tabs-module_size-small-compact__V52Bx","border":"Tabs-module_border__NqdiS","tab":"Tabs-module_tab__ugp4j","selected":"Tabs-module_selected__IxA81","inset-selection-indicator":"Tabs-module_inset-selection-indicator__rAAj8","arrow-left":"Tabs-module_arrow-left__BqBnw","arrow-right":"Tabs-module_arrow-right__cYsZO","hidden":"Tabs-module_hidden__ZLDLC"};
styleInject(css_248z);

function TabList(props) {
  const {
    border = true,
    className: callerClassName,
    insetSelectionIndicator = false,
    tag,
    size = 'default',
    variant = 'default'
  } = props;
  const [selected, onSelect] = useControllableState(props.selected, props.defaultSelected, props.onSelect);
  const viewportClassName = classNames(styles['tab-list'], styles[`size-${size}`], styles[`variant-${variant}`], border && styles['border'], insetSelectionIndicator && styles['inset-selection-indicator']);
  const contextValue = useMemo(() => ({
    menu: false,
    selected,
    size,
    variant,
    onSelect
  }), [selected, size, variant, onSelect]);
  return /*#__PURE__*/jsx(TabContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/jsx(HorizontalScrollable, {
      className: callerClassName,
      tag: tag,
      viewportClassName: viewportClassName,
      viewportRole: "tablist",
      children: props.children
    })
  });
}

function Tab(props) {
  const {
    id,
    disabled,
    children,
    className: callerClassName,
    tag = id
  } = props;
  const {
    menu,
    selected,
    variant,
    size,
    onSelect
  } = useContext(TabContext);
  const isSelected = id === selected;
  const className = classNames('dsx-component', styles['tab'], isSelected && styles['selected'], callerClassName);
  function handleClick() {
    if (!disabled) {
      onSelect(id);
    }
  }
  const buttonVariant = {
    default: 'ui-control',
    inverse: 'inverse-ui-control'
  }[variant];
  return menu ? /*#__PURE__*/jsx(MenuItem, {
    disabled: disabled,
    value: id,
    children: children
  }) : /*#__PURE__*/jsx(Button, {
    "aria-selected": isSelected,
    className: className,
    disabled: disabled,
    role: "tab",
    size: size,
    variant: buttonVariant,
    "data-dsx--tab": tag,
    onClick: handleClick,
    children: children
  });
}

export { Tab, TabList };
